/**
 * Outlapped — API client + data hooks for the editorial pages.
 * Race engine traffic (sessions, replay artifacts, live stream) lives in src/race/api.js.
 */
import axios from "axios";
import { useEffect, useState } from "react";

const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API = `${BACKEND_URL}/api`;

const http = axios.create({
    baseURL: API,
    timeout: 10000,
});

export const AUTH_TOKEN_KEY = "outlapped.auth.token";

export function getAuthToken() {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token) {
    if (typeof window === "undefined") return;
    if (token) window.localStorage.setItem(AUTH_TOKEN_KEY, token);
    else window.localStorage.removeItem(AUTH_TOKEN_KEY);
}

http.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// -----------------------------------------------------------------------------
// Plain fetchers
// -----------------------------------------------------------------------------
export const api = {
    health: () => http.get("/health").then((r) => r.data),

    authSignup: (payload) => http.post("/auth/signup", payload).then((r) => r.data),
    authLogin: (payload) => http.post("/auth/login", payload).then((r) => r.data),
    authMe: () => http.get("/auth/me").then((r) => r.data),
    authLogout: () => http.post("/auth/logout").then((r) => r.data),
    oauthStartUrl: (provider) => `${API}/auth/oauth/${provider}/start`,

    scheduleCurrent: () => http.get("/schedule/current").then((r) => r.data),
    calendarCurrent: () => http.get("/calendar/current").then((r) => r.data),
    scheduleNext: () => http.get("/schedule/next").then((r) => r.data),

    driversActive: () => http.get("/drivers/active").then((r) => r.data),
    driversAll: () => http.get("/drivers").then((r) => r.data),
    driversLegends: () => http.get("/drivers/legends").then((r) => r.data),
    driverById: (id) => http.get(`/drivers/${id}`).then((r) => r.data),
    driverStats: (id) => http.get(`/drivers/${id}/stats`).then((r) => r.data),

    teamsActive: () => http.get("/teams/active").then((r) => r.data),
    teamsAll: () => http.get("/teams").then((r) => r.data),
    teamsLegendary: () => http.get("/teams/legendary").then((r) => r.data),
    teamById: (id) => http.get(`/teams/${id}`).then((r) => r.data),
    teamStats: (id) => http.get(`/teams/${id}/stats`).then((r) => r.data),

    standings: () => http.get("/stats/leaderboards").then((r) => r.data),
    records: () => http.get("/stats/records").then((r) => r.data),
    currentSeason: () => http.get("/stats/current-season").then((r) => r.data),

    news: (limit = 20) =>
        http.get(`/news?limit=${limit}`).then((r) => r.data),
    newsItem: (slugOrId) => http.get(`/news/${slugOrId}`).then((r) => r.data),
    newsRecent: (limit = 6) =>
        http.get(`/news/recent?limit=${limit}`).then((r) => r.data),

    glossary: () => http.get("/glossary").then((r) => r.data),
    featured: () => http.get("/featured").then((r) => r.data),
};

// -----------------------------------------------------------------------------
// Generic data hook
// -----------------------------------------------------------------------------
/**
 * useFetch — lightweight fetch hook with loading/error states.
 * @template T
 * @param {() => Promise<T>} fn
 * @param {Array<unknown>} deps
 */
export function useFetch(fn, deps = []) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fn()
            .then((d) => {
                if (!cancelled) {
                    setData(d);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e);
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { data, loading, error };
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
export function formatLapMs(ms) {
    if (!ms || ms <= 0) return "—";
    const totalSec = ms / 1000;
    const m = Math.floor(totalSec / 60);
    const s = (totalSec - m * 60).toFixed(3);
    return `${m}:${s.padStart(6, "0")}`;
}

function parseNumericGap(value) {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const normalized = trimmed.replace(/s$/i, "").trim();
    if (/^[+-]?\d+(\.\d+)?$/.test(normalized)) {
        const numeric = Number(normalized);
        return Number.isFinite(numeric) ? numeric : null;
    }
    return null;
}

function isLapGapString(value) {
    return typeof value === "string" && /\bLAP(S)?\b/i.test(value.trim());
}

export function formatGap(value) {
    if (value == null) return "—";
    if (typeof value === "object") return "—";
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return "—";
        if (/^leader$/i.test(trimmed)) return "Leader";
        if (isLapGapString(trimmed)) {
            return trimmed.startsWith("+") ? trimmed : trimmed.replace(/^\+?/, "");
        }
        if (/^[+]\d+(\.\d+)?s?$/i.test(trimmed)) {
            const numeric = parseNumericGap(trimmed);
            return numeric == null ? trimmed : `+${numeric.toFixed(3)}`;
        }
    }
    const numeric = parseNumericGap(value);
    if (numeric == null) return "—";
    if (numeric === 0) return "Leader";
    return `+${numeric.toFixed(3)}`;
}
